import { ErrorBoundary, type JSX } from 'solid-js';
import { t } from '../i18n';
import {
  type ClientErrorReporter,
  clientErrorReport,
  reportClientError,
} from '../lib/client-errors';
import { ErrorState } from './ErrorState';

// Top-level render-error boundary for the routed app. A synchronous throw during
// render (query errors are QueryBoundary's job — this catches everything else)
// would otherwise blank the whole SPA with no recovery. It contains the throw to
// a full-page fallback (matching the chrome-less login/outage states, since the
// routed AppShell is gone) and offers a Reset that re-mounts the subtree. The
// fallback drops AppShell — and with it the disclosure footer — so it re-renders
// t('bot.disclosure') here: the bot must always disclose, even on a crash screen.
// Wraps <Router> in App.tsx (audit #48). Every crash it catches goes to the client
// error sink (`lib/client-errors.ts`) before the fallback renders — contained is not
// the same as seen: the console always, and a report for a logged-in session. The
// fallback renders either way. `report` is injectable for tests.
export function AppErrorBoundary(props: {
  readonly children: JSX.Element;
  readonly report?: ClientErrorReporter;
}): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        (props.report ?? reportClientError)(clientErrorReport('render', error));
        return (
          <main class="login">
            <div class="login__fallback">
              {/* Generic on purpose. ErrorState now prefers the thrown reason
                (#801), which is right for an API failure and wrong here: what
                lands in this boundary is a RENDER crash, whose message is a JS
                internal ("undefined is not a function"). Nothing a maintainer
                can act on, so keep the plain copy — the request id below is the
                thing worth quoting to support. */}
              <ErrorState error={error} onRetry={reset} message={t('errors.generic')} />
              <p class="login__disclosure">{t('bot.disclosure')}</p>
            </div>
          </main>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
}
